import PropTypes from 'prop-types';
import { Button, Popover } from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import React, { useEffect, useState } from 'react';
import { DatePickerPopOver } from '../DatePickerUI/datePickerPopOver';
import selectors from '../../../modules/selectors/datePickerSelectors';
import { useDispatch, useSelector } from 'react-redux';
import { TransformDate } from '../DatePickerUtils/datePickerUtils';
import {
  OVERALL_PERFORMANCE,
  AUDIENCE_ANALYSIS,
  CHANNEL_OPPS,
  TIME_PERIOD,
  SUB_BRANCH_DASH_OPPS_BY_CHANNEL,
  OVERALL_PERFORMANCE_SUB_BRANCH,
  PERFORMANCE_OVERVIEW_SUB_BRANCH,
} from '../../../constants/datePickerConstants';
import { calendarActions } from '../../../modules/reducer/calendarSlice';

const TimePeriodPicker = ({ component, period, restrictDataForPreviousYears = false }) => {
  const { overallPerformanceRange } = useSelector(selectors.overallPerformanceCombinedData);
  const { overallPerformanceSubBranchRange } = useSelector(
    selectors.overallPerformanceSubBranchCombinedData
  );
  const { audienceAnalysisRange } = useSelector(selectors.audienceAnalysisCombinedData);
  const { channelOppsRange } = useSelector(selectors.channeledOppsCombinedData);
  const { subDashChannelOppsRange } = useSelector(selectors.subBranchDashOppsByChannelCombinedData);
  const { performanceOverviewSubBranchRange } = useSelector(
    selectors.performanceOverviewSubBranchCombinedData
  );

  const [anchorElForPopOver, setAnchorElForPopOver] = useState(null);
  const [btnText, setBtnText] = useState('');

  const [weeks, setWeeks] = useState([]);
  const [timePeriodPicker, setTimePeriodPicker] = useState(false);
  const dispatch = useDispatch();
  const { transformedDataWithDate } = useSelector(selectors.datePickerCombinedData);
  const styles = {
    parentDiv: {
      position: 'relative',
      bottom: '5px',
    },
    pickerHeader: {
      margin: '0',
      display: 'block',
      padding: '0',
      color: 'rgba(0, 0, 0, 0.6)',
      lineHeight: '1.9em',
      fontSize: '0.8rem',
    },
  };

  const setTimePeriodText = () => {
    switch (component) {
      case OVERALL_PERFORMANCE: {
        const defaultOverallWeekSelection =
          overallPerformanceRange && TransformDate.preDefinedWeekRange(overallPerformanceRange);
        setWeeks(defaultOverallWeekSelection);
        break;
      }
      case OVERALL_PERFORMANCE_SUB_BRANCH: {
        const defaultOverallWeekSelection =
          overallPerformanceSubBranchRange &&
          TransformDate.preDefinedWeekRange(overallPerformanceSubBranchRange);
        setWeeks(defaultOverallWeekSelection);
        break;
      }

      case AUDIENCE_ANALYSIS: {
        const defaultAudienceWeekSelection =
          audienceAnalysisRange && TransformDate.preDefinedWeekRange(audienceAnalysisRange);
        setWeeks(defaultAudienceWeekSelection);
        break;
      }
      case CHANNEL_OPPS: {
        const defaultChannelWeekSelection =
          channelOppsRange && TransformDate.preDefinedWeekRange(channelOppsRange);
        setWeeks(defaultChannelWeekSelection);
        break;
      }
      case SUB_BRANCH_DASH_OPPS_BY_CHANNEL: {
        const defaultChannelWeekSelection =
          subDashChannelOppsRange && TransformDate.preDefinedWeekRange(subDashChannelOppsRange);
        setWeeks(defaultChannelWeekSelection);
        break;
      }
      case PERFORMANCE_OVERVIEW_SUB_BRANCH: {
        const defaultChannelWeekSelection =
          performanceOverviewSubBranchRange &&
          TransformDate.preDefinedWeekRange(performanceOverviewSubBranchRange);
        setWeeks(defaultChannelWeekSelection);
        break;
      }
    }
  };

  const highlightSelectedWeeks = () => {
    if (transformedDataWithDate?.length > 0) {
      switch (component) {
        case OVERALL_PERFORMANCE: {
          const currentQuarter = TransformDate.getCurrentQuarterBasedOnWeek(
            `w${overallPerformanceRange?.start_week}`,
            transformedDataWithDate?.find(
              (data) => data?.currYear === overallPerformanceRange?.start_year
            )
          );
          dispatch(calendarActions?.setCurrentQuarter(currentQuarter));
          dispatch(calendarActions?.setCurrentYear(overallPerformanceRange?.start_year));
          break;
        }
        case OVERALL_PERFORMANCE_SUB_BRANCH: {
          const currentQuarter = TransformDate.getCurrentQuarterBasedOnWeek(
            `w${overallPerformanceSubBranchRange?.start_week}`,
            transformedDataWithDate?.find(
              (data) => data?.currYear === overallPerformanceSubBranchRange?.start_year
            )
          );
          dispatch(calendarActions?.setCurrentQuarter(currentQuarter));
          dispatch(calendarActions?.setCurrentYear(overallPerformanceSubBranchRange?.start_year));
          break;
        }

        case AUDIENCE_ANALYSIS: {
          const currentQuarter = TransformDate.getCurrentQuarterBasedOnWeek(
            `w${audienceAnalysisRange?.start_week}`,
            transformedDataWithDate?.find(
              (data) => data?.currYear === audienceAnalysisRange?.start_year
            )
          );
          dispatch(calendarActions?.setCurrentQuarter(currentQuarter));
          dispatch(calendarActions?.setCurrentYear(audienceAnalysisRange?.start_year));
          break;
        }
        case CHANNEL_OPPS: {
          const currentQuarter = TransformDate.getCurrentQuarterBasedOnWeek(
            `w${channelOppsRange?.start_week}`,
            transformedDataWithDate?.find((data) => data?.currYear === channelOppsRange?.start_year)
          );
          dispatch(calendarActions?.setCurrentQuarter(currentQuarter));
          dispatch(calendarActions?.setCurrentYear(channelOppsRange?.start_year));
          break;
        }
        case SUB_BRANCH_DASH_OPPS_BY_CHANNEL: {
          const currentQuarter = TransformDate.getCurrentQuarterBasedOnWeek(
            `w${subDashChannelOppsRange?.start_week}`,
            transformedDataWithDate?.find(
              (data) => data?.currYear === subDashChannelOppsRange?.start_year
            )
          );
          dispatch(calendarActions?.setCurrentQuarter(currentQuarter));
          dispatch(calendarActions?.setCurrentYear(subDashChannelOppsRange?.start_year));
          break;
        }
        case PERFORMANCE_OVERVIEW_SUB_BRANCH: {
          const currentQuarter = TransformDate.getCurrentQuarterBasedOnWeek(
            `w${performanceOverviewSubBranchRange?.start_week}`,
            transformedDataWithDate?.find(
              (data) => data?.currYear === performanceOverviewSubBranchRange?.start_year
            )
          );
          dispatch(calendarActions?.setCurrentQuarter(currentQuarter));
          dispatch(calendarActions?.setCurrentYear(performanceOverviewSubBranchRange?.start_year));
          break;
        }
      }
    }
  };

  const handleClickForPopOver = (event) => {
    if (btnText === TIME_PERIOD) return null;
    highlightSelectedWeeks();
    setTimePeriodText();
    setAnchorElForPopOver(event.currentTarget);
    setTimePeriodPicker(true);
  };

  const checkForGlobalObject = (object) => {
    if (object === null || object === undefined) {
      return false; // Return false if object is null or undefined
    }
    return (
      Object.values(object).every((value) => value === null || value === undefined) ||
      Object.values(object).some((value) => value === null || value === undefined)
    );
  };

  useEffect(() => {
    switch (component) {
      case OVERALL_PERFORMANCE: {
        const overallPerformanceValuesAreNull = checkForGlobalObject(overallPerformanceRange);
        if (!overallPerformanceValuesAreNull) {
          const value = `w${overallPerformanceRange?.start_week}, ${overallPerformanceRange?.start_year} - w${overallPerformanceRange?.end_week}, ${overallPerformanceRange?.end_year}`;
          setBtnText(value);
        } else {
          setBtnText(period);
        }
        break;
      }
      case OVERALL_PERFORMANCE_SUB_BRANCH: {
        const overallPerformanceValuesAreNull = checkForGlobalObject(
          overallPerformanceSubBranchRange
        );
        if (!overallPerformanceValuesAreNull) {
          const value = `w${overallPerformanceSubBranchRange?.start_week}, ${overallPerformanceSubBranchRange?.start_year} - w${overallPerformanceSubBranchRange?.end_week}, ${overallPerformanceSubBranchRange?.end_year}`;
          setBtnText(value);
        } else {
          setBtnText(period);
        }
        break;
      }
      case AUDIENCE_ANALYSIS: {
        const audienceAnalysisvValuesAreNull = checkForGlobalObject(audienceAnalysisRange);
        if (!audienceAnalysisvValuesAreNull) {
          const value = `w${audienceAnalysisRange?.start_week}, ${audienceAnalysisRange?.start_year} - w${audienceAnalysisRange?.end_week}, ${audienceAnalysisRange?.end_year}`;
          setBtnText(value);
        } else {
          setBtnText(period);
        }
        break;
      }
      case CHANNEL_OPPS: {
        const channelOppsValuesAreNull = checkForGlobalObject(channelOppsRange);
        if (!channelOppsValuesAreNull) {
          const value = `w${channelOppsRange?.start_week}, ${channelOppsRange?.start_year} - w${channelOppsRange?.end_week}, ${channelOppsRange?.end_year}`;
          setBtnText(value);
        } else {
          setBtnText(period);
        }
        break;
      }
      case SUB_BRANCH_DASH_OPPS_BY_CHANNEL: {
        const channelOppsValuesAreNull = checkForGlobalObject(subDashChannelOppsRange);
        if (!channelOppsValuesAreNull) {
          const value = `w${subDashChannelOppsRange?.start_week}, ${subDashChannelOppsRange?.start_year} - w${subDashChannelOppsRange?.end_week}, ${subDashChannelOppsRange?.end_year}`;
          setBtnText(value);
        } else {
          setBtnText(period);
        }
        break;
      }
      case PERFORMANCE_OVERVIEW_SUB_BRANCH: {
        const channelOppsValuesAreNull = checkForGlobalObject(performanceOverviewSubBranchRange);
        if (!channelOppsValuesAreNull) {
          const value = `w${performanceOverviewSubBranchRange?.start_week}, ${performanceOverviewSubBranchRange?.start_year} - w${performanceOverviewSubBranchRange?.end_week}, ${performanceOverviewSubBranchRange?.end_year}`;
          setBtnText(value);
        } else {
          setBtnText(period);
        }
        break;
      }
      default:
    }
  }, [
    overallPerformanceRange,
    overallPerformanceSubBranchRange,
    audienceAnalysisRange,
    channelOppsRange,
    subDashChannelOppsRange,
    performanceOverviewSubBranchRange,
    btnText,
    component,
    period,
  ]);

  const handleCloseForPopOver = () => {
    setAnchorElForPopOver(null);
  };

  const openPopOver = Boolean(anchorElForPopOver);
  const popoverContent = (
    <DatePickerPopOver
      onClose={handleCloseForPopOver}
      component={component}
      weekSelection={weeks}
      timePeriodPicker={timePeriodPicker}
      restrictDataForPreviousYears={restrictDataForPreviousYears}
    />
  );

  return (
    <div style={styles.parentDiv}>
      <p style={styles.pickerHeader}>Time Period</p>
      <Button
        id="timePeriod"
        variant="outlined"
        color="secondary"
        startIcon={<DateRangeIcon />}
        onClick={handleClickForPopOver}
      >
        {btnText}
      </Button>
      <Popover
        open={openPopOver}
        anchorEl={anchorElForPopOver}
        onClose={handleCloseForPopOver}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {popoverContent}
      </Popover>
    </div>
  );
};

// Prop validation
TimePeriodPicker.propTypes = {
  component: PropTypes.element.isRequired,
  period: PropTypes.string.isRequired,
  restrictDataForPreviousYears: PropTypes.bool,
};

export default TimePeriodPicker;
